import React from "react";
import {
  AlertTriangle, Scale, HelpCircle, FileX, Minus, Lock,
  Archive, CheckCircle2, Clock, ShieldCheck, Info, Ban,
} from "lucide-react";

const STATE_CONFIG = {
  LEGAL_BASIS_PENDING: {
    label: "Legal Basis Pending",
    bg: "bg-amber-100",
    text: "text-amber-800",
    border: "border-amber-300",
    icon: AlertTriangle,
    helperText: "Statutory obligation may exist — contribution suspended pending legal basis verification.",
    nextAction: "Statutory Analyst must advance OQ-V4-5 to PRIMARY_SOURCE_VERIFIED.",
  },
  COUNSEL_REVIEW_REQUIRED: {
    label: "Counsel Required",
    bg: "bg-red-100",
    text: "text-red-800",
    border: "border-red-300",
    icon: Scale,
    helperText: "External legal counsel required before any routing or calculation can proceed.",
    nextAction: "Issue GCC counsel questionnaire (OQ-V4-6).",
  },
  CLASSIFICATION_AMBIGUOUS: {
    label: "Classification Ambiguous",
    bg: "bg-purple-100",
    text: "text-purple-800",
    border: "border-purple-300",
    icon: HelpCircle,
    helperText: "Conflicting input facts prevent deterministic regime determination. Analyst investigation required.",
    nextAction: "Statutory Analyst must investigate and create a superseding determination.",
  },
  EVIDENCE_INSUFFICIENT: {
    label: "Evidence Insufficient",
    bg: "bg-orange-100",
    text: "text-orange-800",
    border: "border-orange-300",
    icon: FileX,
    helperText: "Required input facts are absent. Classification or eligibility is suspended.",
    nextAction: "Supply missing input data before classification can proceed.",
  },
  NO_APPLICABLE_REGIME: {
    label: "No Regime Applicable",
    bg: "bg-slate-100",
    text: "text-slate-600",
    border: "border-slate-300",
    icon: Minus,
    helperText: "No statutory pension obligation applies. This is a correct, non-error outcome.",
    nextAction: null,
  },
  BLOCKED: {
    label: "Blocked",
    bg: "bg-red-100",
    text: "text-red-800",
    border: "border-red-400",
    icon: Lock,
    helperText: "Configuration entity blocked pending legal verification or ARB decision.",
    nextAction: "Resolve blocking Open Question before this entity can be activated.",
  },
  SUPERSEDED: {
    label: "Superseded",
    bg: "bg-gray-100",
    text: "text-gray-500",
    border: "border-gray-300",
    icon: Archive,
    helperText: "Replaced by a newer record. Preserved as immutable audit history.",
    nextAction: null,
  },
  // Success / operational states
  ACTIVE: { label: "Active", bg: "bg-green-100", text: "text-green-800", border: "border-green-300", icon: CheckCircle2 },
  EFFECTIVE: { label: "Effective", bg: "bg-green-100", text: "text-green-800", border: "border-green-300", icon: CheckCircle2 },
  APPROVED: { label: "Approved", bg: "bg-teal-100", text: "text-teal-800", border: "border-teal-300", icon: ShieldCheck },
  DRAFT: { label: "Draft", bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300", icon: Clock },
  PENDING: { label: "Pending Review", bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300", icon: Clock },
  DESIGN_READY: { label: "Design Ready", bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-300", icon: Info },
  PROTOTYPE_READY: { label: "Prototype Ready", bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-300", icon: Info },
  PENDING_ARB: { label: "Pending ARB", bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300", icon: Clock },
  PRIMARY_SOURCE_VERIFIED: { label: "Primary Source Verified", bg: "bg-green-100", text: "text-green-800", border: "border-green-300", icon: CheckCircle2 },
  SOURCE_IDENTIFIED: { label: "Source Identified", bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-300", icon: AlertTriangle },
  EVIDENCE_REQUIRED: { label: "Evidence Required", bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-300", icon: FileX },
  APPROVED_FOR_CONFIGURATION: { label: "Approved for Config", bg: "bg-green-100", text: "text-green-800", border: "border-green-300", icon: CheckCircle2 },
  LEGAL_INTERPRETATION_REQUIRED: { label: "Interpretation Required", bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-300", icon: AlertTriangle },
  LEGAL_REVIEW_REQUIRED: { label: "Legal Review Required", bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-300", icon: AlertTriangle },
  PARTIAL: { label: "Partial", bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-300", icon: AlertTriangle },
  NOT_STARTED: { label: "Not Started", bg: "bg-gray-100", text: "text-gray-500", border: "border-gray-300", icon: Minus },
  NOT_REQUIRED: { label: "Not Required", bg: "bg-gray-100", text: "text-gray-500", border: "border-gray-200", icon: Minus },
  OPEN: { label: "Open", bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-300", icon: Clock },
  EXTERNALLY_BLOCKED: { label: "Externally Blocked", bg: "bg-red-100", text: "text-red-800", border: "border-red-300", icon: Ban },
  IMPL_NOT_AUTHORIZED: { label: "Not Authorized", bg: "bg-red-100", text: "text-red-800", border: "border-red-300", icon: Lock },
  ELIGIBLE: { label: "Eligible", bg: "bg-green-100", text: "text-green-800", border: "border-green-300", icon: CheckCircle2 },
  AMBIGUOUS: { label: "Ambiguous", bg: "bg-purple-100", text: "text-purple-800", border: "border-purple-300", icon: HelpCircle },
};

export function StatutoryBadge({ state, size = "sm", showIcon = true, className = "" }) {
  const cfg = STATE_CONFIG[state] || {
    label: state,
    bg: "bg-gray-100",
    text: "text-gray-600",
    border: "border-gray-300",
    icon: Info,
  };
  const Icon = cfg.icon;
  const sizeClass = size === "lg" ? "text-sm px-2.5 py-1 gap-1.5" : "text-xs px-2 py-0.5 gap-1";

  return (
    <span
      className={`inline-flex items-center rounded border font-medium whitespace-nowrap ${cfg.bg} ${cfg.text} ${cfg.border} ${sizeClass} ${className}`}
      data-testid={`badge-${state}`}
    >
      {showIcon && <Icon className={size === "lg" ? "h-3.5 w-3.5 shrink-0" : "h-3 w-3 shrink-0"} />}
      {cfg.label}
    </span>
  );
}

export function StatutoryStatePanel({ state, reason, oqRef, suspensionNotice = false, className = "" }) {
  const cfg = STATE_CONFIG[state];
  if (!cfg) return null;
  const isWarning = ["LEGAL_BASIS_PENDING", "COUNSEL_REVIEW_REQUIRED", "CLASSIFICATION_AMBIGUOUS", "EVIDENCE_INSUFFICIENT", "BLOCKED"].includes(state);

  return (
    <div className={`rounded-lg border p-4 ${cfg.bg} ${cfg.border} ${className}`}>
      <div className="flex items-start gap-3">
        <cfg.icon className={`h-5 w-5 mt-0.5 shrink-0 ${cfg.text}`} />
        <div className="flex-1 min-w-0">
          <div className={`font-semibold text-sm ${cfg.text}`}>{cfg.label}</div>
          {reason && <p className={`mt-1 text-sm ${cfg.text} opacity-90`}>{reason}</p>}
          {!reason && cfg.helperText && <p className={`mt-1 text-sm ${cfg.text} opacity-80`}>{cfg.helperText}</p>}
          {oqRef && (
            <p className={`mt-1.5 text-xs font-mono ${cfg.text} opacity-70`}>
              Open Question: {oqRef}
            </p>
          )}
          {isWarning && suspensionNotice && (
            <p className={`mt-2 text-xs font-semibold ${cfg.text}`}>
              Contributions are SUSPENDED. The obligation is NOT discharged. Back-computation will be required once the basis is resolved.
            </p>
          )}
          {cfg.nextAction && (
            <p className={`mt-2 text-xs ${cfg.text} opacity-70`}>
              Next action: {cfg.nextAction}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export { STATE_CONFIG };
